import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { apiClient, unwrapApiResult, withAbortSignal } from "@/lib/api-client";
import { sortBrandsWithPriority } from "@/lib/brand-priority";
import { useDbCategories } from "@/hooks/use-products";
import type { DbCategory } from "@/types/product";

export interface NavMenuGroup {
  key: string;
  categories: DbCategory[];
  brands: string[];
}

/**
 * Lightweight per-(product_type, brand) aggregation for the mega menu.
 * Selecting only `brand, product_type` across all active products keeps the
 * payload tiny and — unlike a paginated products query — guarantees every
 * category shows its real brand list (bags / balls / accessories / apparel
 * were being cut off by a 120-row limit).
 */
function useNavBrandsByType(enabled: boolean) {
  return useQuery({
    queryKey: ["nav-brands-by-type"],
    enabled,
    staleTime: 5 * 60 * 1000,
    retry: 2,
    retryDelay: 1000,
    queryFn: async ({ signal }) => {
      const result = await apiClient.query<Array<{ brand: string | null; product_type: string | null }>>({
        label: "nav-brands",
        routeTag: "useNavBrandsByType",
        timeoutMs: 15_000,
        signal,
        run: (abortSignal) =>
          withAbortSignal(
            supabase
              .from("products")
              .select("brand,product_type")
              .eq("status", "active")
              .not("brand", "is", null)
              .not("product_type", "is", null),
            abortSignal
          ),
      });
      return (unwrapApiResult(result) ?? []) as Array<{ brand: string | null; product_type: string | null }>;
    },
  });
}

export function useNavMenuData(options?: { includeBrands?: boolean }) {
  const includeBrands = options?.includeBrands ?? false;

  const { data: brandRows = [] } = useNavBrandsByType(includeBrands);
  const { data: categories = [] } = useDbCategories(undefined, { minimalFields: true });

  const grouped = useMemo(() => {
    const map: Record<string, NavMenuGroup> = {};

    for (const c of categories) {
      if (!map[c.product_type]) {
        map[c.product_type] = { key: c.product_type, categories: [], brands: [] };
      }
      map[c.product_type].categories.push(c);
    }

    if (includeBrands) {
      const brandSets: Record<string, Set<string>> = {};
      for (const row of brandRows) {
        const pt = (row.product_type || "").toLowerCase();
        const brand = (row.brand || "").trim();
        if (!pt || !brand) continue;
        if (!brandSets[pt]) brandSets[pt] = new Set();
        brandSets[pt].add(brand);
      }

      for (const [pt, set] of Object.entries(brandSets)) {
        if (!map[pt]) map[pt] = { key: pt, categories: [], brands: [] };
        map[pt].brands = sortBrandsWithPriority(Array.from(set));
      }
    }

    return map;
  }, [brandRows, categories, includeBrands]);

  return grouped;
}
